import { RefObject } from 'react';
import {
  IAssistantRagVectorStorage,
  ICreateAssistantRagVectorStorage,
  IUpdateAssistantRagVectorStorage,
} from '@mytaverse-shared/interfaces/assistantRagStorage';
import type { AssistantModalTheme } from '../AssistantEditorModals/theme';
import {
  ErrorCaption,
  ModalContent,
  RagStorageDialog,
  RagStorageModalBody,
} from './styled';
import { useRagStorageForm } from './hooks/useRagStorageForm';
import DialogHeader from './components/DialogHeader';
import TitleField from './components/TitleField';
import TagsField from './components/TagsField';
import SourceModeToggle from './components/SourceModeToggle';
import TextDataField from './components/TextDataField';
import DocumentUploadField from './components/DocumentUploadField';
import DocSourceInfo from './components/DocSourceInfo';
import FormActions from './components/FormActions';

export interface IAssistantRagStorageDialogProps {
  open: boolean;
  assistantRagVectorStorage?: IAssistantRagVectorStorage | null;
  tag?: string;
  tags?: string[];
  title?: string;
  mapName?: string;
  hideArea?: boolean;
  paperRef?: RefObject<HTMLDivElement | undefined>;
  modalTheme?: AssistantModalTheme;
  onClose: () => void;
  onSave: (
    values: ICreateAssistantRagVectorStorage | IUpdateAssistantRagVectorStorage,
  ) => Promise<void>;
}

const AssistantRagStorageDialog = ({
  open,
  assistantRagVectorStorage,
  tag,
  tags,
  title,
  mapName,
  hideArea,
  paperRef,
  modalTheme = 'light',
  onSave,
  onClose,
}: IAssistantRagStorageDialogProps) => {
  const {
    formik,
    allTags,
    isLoading,
    isSubmitting,
    sourceMode,
    selectedFile,
    fileError,
    uploadPct,
    submitError,
    isEditMode,
    isDocBased,
    fileInputRef,
    handleSourceModeChange,
    handleFileChange,
    isCreateButtonDisabled,
    handleFormSubmit,
  } = useRagStorageForm({
    assistantRagVectorStorage,
    tag,
    tags,
    title,
    mapName,
    onSave,
  });

  return (
    <RagStorageDialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') {
          return;
        }
        onClose();
      }}
      fullWidth
      $theme={modalTheme}
      PaperProps={{ ref: paperRef }}
    >
      <ModalContent>
        <DialogHeader isEditMode={isEditMode} onClose={onClose} />

        <RagStorageModalBody>
          <TitleField
            hideArea={hideArea}
            value={formik.values.title}
            onChange={(value) => formik.setFieldValue('title', value)}
          />

          <TagsField
            allTags={allTags}
            value={formik.values.tags}
            lockedTag={tag}
            tags={tags}
            title={title}
            hideArea={hideArea}
            touched={formik.touched.tags as boolean}
            error={formik.errors.tags as string}
            onChange={(newTags) => formik.setFieldValue('tags', newTags)}
            onBlur={() => formik.setFieldTouched('tags')}
          />

          {!isEditMode && (
            <SourceModeToggle
              value={sourceMode}
              onChange={handleSourceModeChange}
            />
          )}

          {!isEditMode && sourceMode === 'text' && (
            <TextDataField
              isLoading={isLoading}
              value={formik.values.textData}
              touched={formik.touched.textData}
              error={formik.errors.textData}
              onChange={formik.handleChange}
              onBlur={() => formik.setFieldTouched('textData')}
            />
          )}

          {!isEditMode && sourceMode === 'document' && (
            <DocumentUploadField
              fileInputRef={fileInputRef}
              selectedFile={selectedFile}
              fileError={fileError}
              isSubmitting={isSubmitting}
              uploadPct={uploadPct}
              onChange={handleFileChange}
            />
          )}

          {isEditMode && isDocBased && (
            <DocSourceInfo
              sourceFileName={assistantRagVectorStorage?.sourceFileName}
            />
          )}

          {isEditMode && (
            <TextDataField
              isLoading={isLoading}
              value={formik.values.textData}
              touched={formik.touched.textData}
              error={formik.errors.textData}
              onChange={formik.handleChange}
              onBlur={() => formik.setFieldTouched('textData')}
            />
          )}

          {submitError && (
            <ErrorCaption variant="caption" color="error">
              {submitError}
            </ErrorCaption>
          )}

          <FormActions
            isEditMode={isEditMode}
            isSubmitting={isSubmitting}
            disabled={isCreateButtonDisabled()}
            onCancel={onClose}
            onSubmit={handleFormSubmit}
          />
        </RagStorageModalBody>
      </ModalContent>
    </RagStorageDialog>
  );
};

export default AssistantRagStorageDialog;
